import React, { useState } from 'react';
import { NOTIFICATION_TYPES, TutorType } from '../../../constants/constants';
import { getNotificationTypeText } from '../../../utils/chat_utils';
import { useChat } from '../../../contexts/ChatContext';
import { ChatType } from '../../../contexts/ChatContext';
import { useSearchParams, useNavigate } from 'react-router-dom';

const errorTextColor = "text-red-500";
const focusRingColor = "focus:ring-indigo-500 focus:border-indigo-500";
const inputPadding = "px-4 py-2";

const NotificationTemplate = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const typeFromUrl = searchParams.get('type');
  
  const { sendMessage } = useChat();

  const [formData, setFormData] = useState({
    messageType: typeFromUrl === 'ai_chat' ? NOTIFICATION_TYPES.AI_CHAT : NOTIFICATION_TYPES.REGULAR,
    schoolId: '',
    title: '',
    body: '',
    target: '',
    detailedBody: '',
    imageUrl: '',
    buttonText: '',
    command: '',
    tutorType: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const generateNotificationText = () => {
    const {
      messageType,
      schoolId,
      title,
      body,
      target,
      detailedBody,
      imageUrl,
      buttonText,
      command,
      tutorType
    } = formData;

   

    let text = `Send a notification`;
    
    if (target) text += ` ${target}`;
    
    text += ' with the following details:\n';
    text+=`messageType: "${messageType}"\n`;
    if (tutorType) text += `tutorType: "${tutorType}"\n`;
    if (schoolId) text += `schoolId: "${schoolId}"\n`;
    text += `title: "${title}"\n`;
    text += `body: "${body}"\n`;
    if (imageUrl) text += `imageUrl: "${imageUrl}"\n`;
    if (detailedBody) text += `detailedBody: "${detailedBody}"\n`;
    if (command) text += `command: "${command}"\n`;
    if (buttonText) text += `buttonText: "${buttonText}"`;
    
    return text;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.notificationType) {
        newErrors.type = 'Please select a Notification Type';
      }

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.command.trim() && formData.notificationType === NOTIFICATION_TYPES.AI_CHAT) {
      newErrors.command = 'Command is required';
    }

    if (!formData.body.trim()) {
      newErrors.body = 'Message is required';
    }

    if (!formData.schoolId.trim()) {
        newErrors.schoolId = 'Message is required';
      }

    if (!formData.target.trim()) {
      newErrors.target = 'Please specify the target.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    const text = generateNotificationText();
    if (text) console.log(text);
    navigate(-1);
    sendMessage(ChatType.AGENT, text);
  };

  return (
    <div className="h-screen w-full flex flex-col bg-gradient-to-b from-[#1F2937] to-black overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-gray-900">
      <form className="m-4 p-6 bg-zinc-900 rounded-lg shadow-lg mx-auto w-full max-w-2xl space-y-4">
        {/* Type of notification - AI Chat or Regular */}
        <div>
          <label className="block text-white mb-2">Notification Type *</label>
          <select
            name="notificationType"
            value={formData.notificationType}
            onChange={handleChange}
            className="w-full bg-zinc-800 text-white rounded p-2 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Select type</option>
            <option value={NOTIFICATION_TYPES.AI_CHAT}>{getNotificationTypeText(NOTIFICATION_TYPES.AI_CHAT)}</option>
            <option value={NOTIFICATION_TYPES.REGULAR}>{getNotificationTypeText(NOTIFICATION_TYPES.REGULAR)}</option>
            <option value={NOTIFICATION_TYPES.ATTENDANCE}>{getNotificationTypeText(NOTIFICATION_TYPES.ATTENDANCE)}</option>
            <option value={NOTIFICATION_TYPES.NOTICE}>{getNotificationTypeText(NOTIFICATION_TYPES.NOTICE)}</option>
            <option value={NOTIFICATION_TYPES.TUTOR}>{getNotificationTypeText(NOTIFICATION_TYPES.TUTOR)}</option>
          </select>
          {errors.type && (
            <p className={`mt-1 text-sm ${errorTextColor}`}>{errors.type}</p>
          )}
        </div>

        {/* School identifier for school-specific notifications */}
        <div>
          <label className="block text-white mb-2">School ID *</label>
          <input
            type="text"
            name="schoolId"
            value={formData.schoolId}
            onChange={handleChange}
            className={`w-full bg-zinc-800 text-white rounded p-2 border border-zinc-700 focus:outline-none focus:ring-2 ${focusRingColor} ${inputPadding}`}
          />
            {errors.schoolId && (
            <p className={`mt-1 text-sm ${errorTextColor}`}>{errors.schoolId}</p>
          )}
        </div>

        {/* Main notification title */}
        <div>
          <label className="block text-white mb-2">Title *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border-zinc-700 bg-zinc-800 text-white shadow-sm ${focusRingColor} ${inputPadding}`}
            required
          />
          {errors.title && (
            <p className={`mt-1 text-sm ${errorTextColor}`}>{errors.title}</p>
          )}
        </div>

        {/* Brief notification message */}
        <div>
          <label className="block text-white mb-2">Body *</label>
          <input
            type="text"
            name="body"
            value={formData.body}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-md border-zinc-700 bg-zinc-800 text-white shadow-sm ${focusRingColor} ${inputPadding}`}
            required
          />
          {errors.body && (
            <p className={`mt-1 text-sm ${errorTextColor}`}>{errors.body}</p>
          )}
        </div>

        {/* Target audience or recipients for the notification */}
        <div>
          <label className="block text-white mb-2">Target</label>
          <textarea
            name="target"
            value={formData.target}
            onChange={handleChange}
            className="w-full bg-zinc-800 text-white rounded p-2 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows="3"
          />
          {errors.target && (
            <p className={`mt-1 text-sm ${errorTextColor}`}>{errors.target}</p>
          )}
        </div>

        {/* Extended description or additional information */}
        <div>
          <label className="block text-white mb-2">Detailed Body</label>
          <textarea
            name="detailedBody"
            value={formData.detailedBody}
            onChange={handleChange}
            className="w-full bg-zinc-800 text-white rounded p-2 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows="3"
          />
        </div>

        {/* URL for any associated image */}
        <div>
          <label className="block text-white mb-2">Image URL</label>
          <input
            type="text"
            name="imageUrl"
            value={formData.imageUrl}
            onChange={handleChange}
            className="w-full bg-zinc-800 text-white rounded p-2 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Command for AI Chat Only if notificationType is AI Chat */}
        {formData.notificationType === NOTIFICATION_TYPES.AI_CHAT && (
          <div>
            <label className="block text-white mb-2">Command</label>
            <input
              type="text"
              name="command"
              value={formData.command}
              onChange={handleChange}
              className="w-full bg-zinc-800 text-white rounded p-2 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {/* Tutor Type selection for Tutor notifications */}
        {formData.notificationType === NOTIFICATION_TYPES.TUTOR && (
          <div>
            <label className="block text-white mb-2">Tutor Type</label>
            <select
              name="tutorType"
              value={formData.tutorType}
              onChange={handleChange}
              className="w-full bg-zinc-800 text-white rounded p-2 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Tutor Type</option>
              <option value={TutorType.MATH}>Math</option>
              <option value={TutorType.ENGLISH}>English</option>
            </select>
          </div>
        )}

        {/* Text to display on the notification button */}
        <div>
          <label className="block text-white mb-2">Button Text</label>
          <input
            type="text"
            name="buttonText"
            value={formData.buttonText}
            onChange={handleChange}
            className="w-full bg-zinc-800 text-white rounded p-2 border border-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          DONE
        </button>
      </form>
    </div>
  );
};

export default NotificationTemplate;

